import React, {ReactNode, useEffect, useState} from "react";
import {getTranslator} from "../../../translate";
import {AddFacilityUserInput, AddFactoryUserInput, DysonSphere, FacilityType, Factory, FactoryGameType, IFacility, ItemStack, Satisfactory, SpecificFactoryData} from "../../types";
import Radio from "../../../react/components/ui/spectre/Radio";
import Input from "../../../react/components/ui/spectre/Input";
import {Button, ButtonGroup, ButtonTypes} from "../../../react/components/ui/spectre/buttons";
import {Dropdown} from "../../../react/components/ui/spectre/Dropdown";
import {ModalHeader} from "../../../react/components/ui/spectre/ModalHeader";
import {ModalBody} from "../../../react/components/ui/spectre/ModalBody";
import {Modal} from "../../../react/components/ui/spectre/Modal";
import {ModalFooter} from "../../../react/components/ui/spectre/ModalFooter";
import {FacilityIOSummary} from "../FacilityIOSummary";
import FacilityPath from "../FacilityPath";
import {OptionalId} from "../../../index";
import FactoryGameDataFactory from "./FactoryGameData/FactoryGameDataFactory";
import {getAllRecipes, getRecipe, getRecipesForProduct} from "../../Helper/Recipes";
import ItemImage from "../ItemImage";
import FacilityDeleteModal from "../FacilityDeleteModal";
import WithTooltip, {TooltipLocation} from "../../../react/components/ui/withTooltip";
import Recipe from "../Recipe";
import './Modal.sass';
import '../Shared.sass';

const translate = getTranslator('react/component/Satisfactory/General');
const translateDialogue = getTranslator('react/component/Satisfactory/FacilityAddEditModal');

type Props = {
    facility?: IFacility,
    save: (facility: AddFacilityUserInput|AddFactoryUserInput) => void | Promise<void>,
    close: () => void,
    delete: () => Promise<void>,
    parentFacility?: IFacility,
    targetProduct?: ItemStack,
}

export default function FacilityAddEditModal({ facility, save, close, ...props }: Props) {
    const [init, setInit] = useState<boolean>(false);
    const [facilityType, setFacilityType] = useState<FacilityType>();
    const [name, setName] = useState<string>();
    const [hasSetName, setHasSetName] = useState<boolean>(false);
    const [recipe, setRecipe] = useState<string|null>();
    const [amount, setAmount] = useState<number|null>();
    const [parentFacility, setParentFacility] = useState<null|IFacility>();
    const [factoryGameData, setFactoryGameData] = useState<SpecificFactoryData>();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const isValid = name !== '' && (facilityType === 'Facility' || (recipe !== null && amount !== null && amount > 0));
    const isEditing = facility !== undefined;

    useEffect(() => {
        const parentFacility = facility?.parentFacility ?? props.parentFacility;
        const defaultType = (props.targetProduct && 'Factory') || undefined;
        setFacilityType(facility?.type ?? defaultType ?? 'Facility');
        setName(facility?.name ?? translateDialogue('defaultNewName'));
        setParentFacility(parentFacility);
        if (facility?.type === 'Factory') {
            setRecipe(facility.recipeType);
            setAmount(facility.amount);
            switch (facility.game) {
                case FactoryGameType.Satisfactory:
                    setFactoryGameData({
                        clockSpeed: (facility as Factory<Satisfactory>).clockSpeed,
                    });
                    break;
                case FactoryGameType.DysonSphere:
                    const dspFacility = facility as Factory<DysonSphere>;
                    setFactoryGameData({
                        machineTier: dspFacility.machineTier,
                        proliferatorTier: dspFacility.proliferatorTier,
                        proliferatorType: dspFacility.proliferatorType,
                    });
                    break;
            }
        } else {
            setRecipe(null);
            setAmount(1);
            setFactoryGameData(parentFacility?.game === FactoryGameType.Satisfactory ? { clockSpeed: 1 } : { machineTier: 1, proliferatorTier: 0, proliferatorType: null });
        }
        if (props.targetProduct && parentFacility) {
            const anyRecipe = getRecipesForProduct(parentFacility.game, props.targetProduct.item)[0];
            setRecipe(anyRecipe?.recipeType ?? null);
            setName(props.targetProduct.item);
        }
        setInit(true);
    }, [facility]);

    useEffect(() => {
        // If we're editing, we take parent facility from the facility-to-edit
        if (!isEditing) {
            setParentFacility(props.parentFacility);
        }
    }, [props.parentFacility])

    useEffect(() => {
        if (!props.targetProduct) {
            return;
        }

        const myRecipe = getRecipe(gameType, recipe);
        if (!myRecipe) {
            return;
        }

        const outputAmount = myRecipe.products.find(product => product.item === props.targetProduct.item)?.amount ?? 0;
        if (outputAmount === 0) {
            return;
        }

        const recipeRequiredFrequency = props.targetProduct.amount / outputAmount;
        const machinesRequired = recipeRequiredFrequency * myRecipe.duration / 60;
        const integerAmount = Math.ceil(machinesRequired);

        setAmount(integerAmount);
        if (parentFacility?.game === FactoryGameType.Satisfactory) {
            setFactoryGameData({
                clockSpeed: machinesRequired / integerAmount,
            });
        }
    }, [recipe, props.targetProduct]);

    function setNameByUser(name: string): void {
        setName(name);
        setHasSetName(true);
    }

    function setRecipeByUser(recipe: string): void {
        setRecipe(recipe);
        if (!hasSetName) {
            setName(recipe);
        }

    }

    if (!init) {
        return <></>
    }

    const gameType = facility?.game ?? parentFacility.game;

    const recipeSelectionOptions = getAllRecipes(gameType).sort((a, b) => {
        switch (true) {
            // Stuff with no products to the bottom
            case a.products.length === 0 && b.products.length === 0: return a.recipeType.localeCompare(b.recipeType);
            case a.products.length === 0: return -1;
            case b.products.length === 0: return +1;
            // If recipeType === primary product name ==> Default Recipe ==> Show first
            case a.products[0].item === b.products[0].item && a.products[0].item === a.recipeType: return -1;
            case a.products[0].item === b.products[0].item && b.products[0].item === b.recipeType: return +1;
            // If not default recipe, order alphabetically
            case a.products[0].item === b.products[0].item: return a.recipeType.localeCompare(b.recipeType);
            // Sort by item name
            default: return a.products[0].item.localeCompare(b.products[0].item);
        }
    }).reduce((a, data) => {
        a[data.recipeType] = <WithTooltip fullWidth={true} tooltip={<Recipe recipe={data} gameType={gameType} />} location={TooltipLocation.RIGHT}>
            <div className='itemWithName'>
                <div>{data.products.length > 0 && <ItemImage item={data.products[0].item} gameType={gameType} />}</div>
                <div>{data.recipeType}</div>
            </div>
        </WithTooltip>;
        return a;
    }, {} as Record<string, ReactNode>);

    function matchesSearchTerm(recipeName: string, searchTerm: string): boolean {
        const recipe = getRecipe(gameType, recipeName);
        if (props.targetProduct && !recipe.products.some(product => product.item === props.targetProduct.item)) {
            return false;
        }
        if (recipeName.toLowerCase().includes(searchTerm.toLowerCase())) {
            return true;
        }
        const primaryProduct = recipe.products[0];
        if (!primaryProduct) {
            return false;
        }
        return primaryProduct.item.toLowerCase().includes(searchTerm.toLowerCase());
    }

    async function saveHandler() {
        if (!isValid || isSaving) {
            return;
        }
        setIsSaving(true);
        await save({
            type: facilityType,
            name,
            recipeType: recipe,
            amount,
            ...factoryGameData
        });
        setIsSaving(false);
        close();
    }

    async function deleteHandler() {
        await props.delete();
    }

    const virtualFacilityCore: OptionalId<IFacility> = {
        type: facilityType,
        name: name,
        game: gameType,
        parentFacility: parentFacility ?? null,
        facilities: [],
    };
    const virtualFacility: OptionalId<IFacility> = facilityType === 'Facility'
        ? virtualFacilityCore
        : {
            recipeType: recipe,
            recipe: recipe !== null ? getRecipe(gameType, recipe) : null,
            amount,
            ...virtualFacilityCore,
            ...factoryGameData,
        } as OptionalId<Factory>;

    // For Satisfactory, you can change the speed of machines.
    // We display something like "this is equivalent to 16x 100% + 1x 33.33%"
    // or "this is equivalent to 6x 250% + 133.33%".

    let clockSpeedEquivalent: null | { fullAmount: number, fullSpeed: number, partialSpeed: number, } = null;

    if (gameType === FactoryGameType.Satisfactory) {
        const speed = (factoryGameData as SpecificFactoryData<Satisfactory>).clockSpeed;
        const fullSpeed = speed > 1 ? 2.5 : 1;
        const fullAmount = Math.floor(amount * speed / fullSpeed);
        const partialSpeed = (amount * speed - fullAmount * fullSpeed);

        if (fullAmount > 0 && partialSpeed > 0) {
            clockSpeedEquivalent = {
                fullSpeed,
                fullAmount,
                partialSpeed,
            }
        }
    }

    return <Modal onClose={() => close()}>
        <ModalHeader showCloseButton={true} title={translateDialogue(isEditing ? 'editFacility' : 'addFacility')} onClose={() => close()} />
        <ModalBody>
            <div>
                <FacilityPath facility={virtualFacility} excludeRoot={true} />
            </div>
            <div>
                <Input
                    onChange={e => setNameByUser(e.target.value)}
                    value={name}
                    placeholder={translateDialogue('pleaseEnterName')}
                />
            </div>
            <div className='facilityTypeContainer'>
                <Radio
                    disabled={isEditing}
                    options={{ Facility: translate('facility'), Factory: translate('factory') }}
                    value={facilityType}
                    name='facilityType'
                    onChange={newFacilityType => setFacilityType(newFacilityType as FacilityType)}
                />
            </div>
            {
                facilityType === 'Factory' && <div>
                    <Dropdown
                        buttonTypes={[]}
                        value={recipe}
                        onChange={setRecipeByUser}
                        options={recipeSelectionOptions}
                        placeholder={translateDialogue('factorySelectRecipe')}
                        searchable={true}
                        matchesSearchTerm={matchesSearchTerm}
                    />

                    {props.targetProduct && <div className='py-1'>
                        <div dangerouslySetInnerHTML={{__html: translateDialogue('productionTarget', {AMOUNT: props.targetProduct.amount.toLocaleString('de-DE', { maximumFractionDigits: 4 }), ITEM: props.targetProduct.item})}} />
                    </div>}

                    <div className='inputGroup mt-1'>
                        <div>{translateDialogue('amount')}</div>
                        <div>
                            <Input type='number' step={1} min={1}
                                onChange={(e) => e.target.value === '' ? setAmount(null) : setAmount(Number(e.target.valueAsNumber))}
                                value={amount ?? ''}
                            />
                        </div>

                        <FactoryGameDataFactory gameType={gameType} factoryGameData={factoryGameData} setFactoryGameData={setFactoryGameData} translateDialogue={translateDialogue} />
                    </div>

                    {gameType === FactoryGameType.Satisfactory && clockSpeedEquivalent && <div>
                        {translateDialogue('clockSpeedEquivalent', {
                            FULL_AMOUNT: clockSpeedEquivalent.fullAmount.toString(),
                            FULL_SPEED: (100 * clockSpeedEquivalent.fullSpeed).toLocaleString('de-DE', { maximumFractionDigits: 4 }),
                            PARTIAL_SPEED: (100 * clockSpeedEquivalent.partialSpeed).toLocaleString('de-DE', { maximumFractionDigits: 4 }),
                        })}
                    </div>}

                    {virtualFacility.type === 'Factory' && isValid && <div className='mt-1'><FacilityIOSummary facility={virtualFacility} /></div>}
                </div>
            }
            { isDeleting && <FacilityDeleteModal delete={() => deleteHandler()} close={() => setIsDeleting(false)} facility={facility} /> }
        </ModalBody>
        <ModalFooter>
            <div className='buttonContainer'>
                { isEditing &&
                    <ButtonGroup>
                        <Button onClick={() => setIsDeleting(true)}
                                buttonTypes={[ButtonTypes.error]}>{translateDialogue('delete')}</Button>
                    </ButtonGroup>
                }
                <ButtonGroup>
                    <Button onClick={() => saveHandler()} buttonTypes={[ButtonTypes.success]} disabled={!isValid || isSaving}>{translateDialogue('save')}</Button>
                </ButtonGroup>
            </div>
        </ModalFooter>
    </Modal>
}
